#!/usr/bin/env node
import { Command } from "commander";
import * as p from "@clack/prompts";
import pc from "picocolors";
import { resolve } from "node:path";
import { Brand, CARRIER_BRAND, deriveAccentDark } from "./lib/brand.js";
import {
  installPlugin,
  installAuthGuidance,
  manualCommands,
  MCP_URL,
} from "./lib/plugin.js";
import { scaffoldStorefront } from "./lib/whitelabel.js";
import { writeStorefrontLogo } from "./lib/logo.js";
import { buildSite, deploySite, installDeps, loadStorefrontBrand, slug } from "./lib/site.js";
import {
  isTargetId,
  probeAll,
  rankTargets,
  rollback,
  TARGET_IDS,
  type TargetId,
} from "./lib/deploy-targets.js";
import { formatProbes, repairPlanFor, verifyStorefront } from "./lib/verify-storefront.js";
import {
  configureClerkInstance,
  provisionClerk,
  storefrontClerkUrls,
} from "./lib/clerk.js";
import { clerkCliDeps } from "./lib/clerk-cli.js";
import { mergeEnvLocal } from "./lib/storefront-secrets.js";
import { provisionResend, startLoopbackServer, RESEND_DOMAINS_URL } from "./lib/resend.js";
import { renderTui } from "@carrier/screens";
import {
  buildDashScreen,
  DASH_DOMAINS,
  DASH_TOOL,
  isDashDomain,
  screenFromStructured,
} from "./lib/dash.js";
import { exists } from "./lib/fsx.js";
import { gatherStatus } from "./lib/status.js";
import {
  formatStatusBlock,
  formatNextSteps,
  formatNlExamples,
  oauthFirstUseNote,
  accountLinksNote,
  withNextStep,
} from "./lib/guidance.js";
import { runHome, printStatus } from "./lib/home.js";
import { carrierAsk, callMcpTool } from "./lib/ask.js";
import { login as oauthLogin, logout as oauthLogout } from "./lib/auth.js";
import { signup as headlessSignup, type SignupIO } from "./lib/signup.js";
import { CLI_DOMAINS } from "./lib/domains.js";
import { runCapability, runRawTool, type CapabilityFlags } from "./lib/capability.js";
import { openUrl, SIGN_UP_URL, SIGN_IN_URL, CONSOLE_URL, ONBOARDING_URL, MCP_HOME } from "./lib/urls.js";
import { CARRIER_VERSION } from "../version.js";

const VERSION = CARRIER_VERSION;

function header(): void {
  p.intro(`${pc.bold(pc.yellow("◆ carrier"))} ${pc.dim("· the Stripe of telecom — CLI v" + VERSION)}`);
}

function ok(msg: string): void {
  p.log.success(pc.green(msg));
}
function info(msg: string): void {
  p.log.info(msg);
}
function fail(msg: string, next: string[]): void {
  p.log.error(msg);
  p.note(next.map((s) => `→ ${s}`).join("\n"), "What to do next");
}

/** Interactive brand capture, seeded from Carrier defaults. */
async function promptBrand(seed: Brand): Promise<Brand> {
  const name = (await p.text({
    message: "Brand name",
    placeholder: seed.name,
    defaultValue: seed.name,
  })) as string;
  if (p.isCancel(name)) process.exit(0);

  const domain = (await p.text({
    message: "Domain",
    placeholder: seed.domain,
    defaultValue: seed.domain,
  })) as string;
  if (p.isCancel(domain)) process.exit(0);

  const accent = (await p.text({
    message: "Accent color (hex)",
    placeholder: seed.colors.accent,
    defaultValue: seed.colors.accent,
  })) as string;
  if (p.isCancel(accent)) process.exit(0);

  const supportEmail = (await p.text({
    message: "Support email",
    placeholder: `support@${domain}`,
    defaultValue: `support@${domain}`,
  })) as string;
  if (p.isCancel(supportEmail)) process.exit(0);

  const accentDark = deriveAccentDark(accent, seed);
  const isCarrier = name === CARRIER_BRAND.name && domain === CARRIER_BRAND.domain;
  return {
    ...seed,
    name,
    legalName: name,
    tagline: isCarrier ? seed.tagline : `Mobile connectivity by ${name}.`,
    description: isCarrier
      ? seed.description
      : `Mobile data from ${name}. Instant QR activation, no contract.`,
    domain,
    supportEmail,
    supportUrl: `https://${domain}/help`,
    supportWhatsapp: isCarrier ? seed.supportWhatsapp : "",
    emailFromName: name,
    appUrl: `https://${domain}`,
    social: isCarrier ? seed.social : {},
    colors: { ...seed.colors, accent, accentDark },
  };
}

async function doPluginInstall(): Promise<void> {
  const s = p.spinner();
  s.start("Installing Carrier Claude Code plugin + zero-cred MCP");
  let r;
  try {
    r = await installPlugin();
  } catch (e: unknown) {
    s.stop("Install failed");
    fail(String(e instanceof Error ? e.message : e), [
      "Reinstall the package: npm i -g @carrierllc/mcp  (or npx -y -p @carrierllc/mcp carrier plugin install)",
      "Or finish manually with the commands under `carrier plugin install` help",
      `Sign up anytime: ${SIGN_UP_URL}`,
    ]);
    return;
  }
  s.stop("Plugin staged");
  ok(`Plugin → ${r.copiedTo}`);
  info(
    `MCP: ${r.mcpAdded ? pc.green("registered") : pc.yellow("manual")}  ·  ` +
      `marketplace: ${r.marketplaceAdded ? pc.green("added") : pc.yellow("manual")}  ·  ` +
      `install: ${r.pluginInstalled ? pc.green("done") : pc.yellow("manual")}`,
  );
  p.note(installAuthGuidance().join("\n"), "OAuth on first use (Entry A)");
  if (!r.claudeFound || r.notes.length) {
    p.note(manualCommands().join("\n"), "Finish wiring (run in your terminal)");
    if (r.notes.length) info(pc.dim(r.notes.join("\n")));
    p.note(
      [
        "You can still create an account now:",
        `  carrier open signup`,
        "Then open Claude and talk to your fleet — browser OAuth completes auth.",
      ].join("\n"),
      "Next",
    );
  } else {
    p.note(
      [
        "Open Claude Code and say something like:",
        '  "Show my fleet health"',
        "First call opens the browser for sign-in / sign-up. No token paste.",
        "",
        "More prompts: carrier examples",
      ].join("\n"),
      "Talk to your fleet",
    );
  }
}

async function doSiteCreate(target: string, brand: Brand): Promise<void> {
  const s = p.spinner();
  s.start(`Scaffolding ${brand.name} storefront → ${target}`);
  try {
    await scaffoldStorefront(target, brand);
  } catch (e: unknown) {
    s.stop("Scaffold failed");
    fail(String(e instanceof Error ? e.message : e), [
      "Pick a free directory: carrier site create ./my-storefront",
      "Ensure the package templates shipped with @carrierllc/mcp",
      "Still stuck? carrier open console",
    ]);
    throw e;
  }
  s.stop("Storefront scaffolded");
  ok(`Created ${target} (white-labeled: ${brand.name}, accent ${brand.colors.accent})`);
  const logoSpin = p.spinner();
  logoSpin.start("Generating storefront logo");
  try {
    const logo = await writeStorefrontLogo(target, brand);
    logoSpin.stop(`Logo ready (${logo.source})`);
    ok(logo.path);
  } catch (e: unknown) {
    logoSpin.stop("Logo skipped");
    info(pc.yellow(String(e instanceof Error ? e.message : e)));
  }
}

/**
 * Resolve which target to build for before building, because Cloudflare needs a
 * different build script (`cf:build`) than everything else. With no explicit
 * preference this is the same target the deploy step will pick.
 */
async function resolveBuildTarget(
  storefront: string,
  preferred?: TargetId,
): Promise<TargetId> {
  if (preferred) return preferred;
  const ranked = rankTargets(await probeAll(storefront));
  return ranked[0]?.id ?? "cloudflare";
}

async function maybeBuildDeploy(
  target: string,
  brand: Brand,
  opts: {
    install: boolean;
    build: boolean;
    deploy: boolean;
    preferred?: TargetId;
    pushSecrets?: boolean;
    clerk?: boolean;
    verify?: boolean;
    autofixAgent?: boolean;
    customDomain?: boolean;
  },
): Promise<void> {
  if (opts.install) {
    const s = p.spinner();
    s.start("Installing storefront dependencies");
    const oki = await installDeps(target);
    s.stop(oki ? "Dependencies installed" : "Dependency install reported errors");
    if (!oki) {
      p.note(
        [
          `cd ${target} && pnpm install`,
          "Fix any Node/pnpm version issues, then retry build",
        ].join("\n"),
        "Next",
      );
      return;
    }
  }
  const buildTarget = await resolveBuildTarget(target, opts.preferred);

  if (opts.clerk) await runClerkStep(target, brand);

  if (opts.build) {
    const s = p.spinner();
    s.start(`Building storefront for ${buildTarget}`);
    const okb = await buildSite(target, buildTarget);
    s.stop(okb ? "Build succeeded" : "Build failed — see output above");
    if (!okb) {
      p.note(
        [`cd ${target}`, "pnpm build", "Check env keys in .env.local if the build mentions Clerk"].join("\n"),
        "Next",
      );
      return;
    }
  }
  if (opts.deploy) {
    const s = p.spinner();
    s.start("Deploying");
    const r = await deploySite(target, brand, {
      preferred: opts.preferred,
      pushSecrets: opts.pushSecrets,
      customDomain: opts.customDomain,
    });
    s.stop(r.ok ? `Deployed to ${r.target}: ${r.url ?? r.projectName}` : "Deploy skipped");

    if (!r.ok) {
      if (r.reason) info(pc.yellow(r.reason));
      const hints = r.statuses
        .filter((t) => !t.ready && t.reason)
        .map((t) => `${t.label}: ${t.reason}`);
      p.note(
        [...(hints.length ? hints : ["Log in to a supported host, then retry."]), `Then: carrier site deploy ${target}`].join("\n"),
        "Next",
      );
      return;
    }

    if (r.secrets) {
      if (r.secrets.pushed.length) info(pc.dim(`Secrets staged: ${r.secrets.pushed.join(", ")}`));
      if (r.secrets.failed.length) {
        info(pc.yellow(`Secrets that failed to stage: ${r.secrets.failed.join(", ")}`));
      }
    }
    // The catalog pages throw without this, and the failure only shows at runtime.
    if (!r.secrets?.pushed.includes("CARRIER_API_KEY")) {
      info(
        pc.yellow(
          "CARRIER_API_KEY was not staged — / and /shop will return 500. Set it in .env.local and redeploy.",
        ),
      );
    }
    if (r.url) info(pc.green(r.url));

    // Second Clerk pass: only now does the deployed origin exist to whitelist.
    if (opts.clerk && r.url) {
      const secretKey = process.env.CLERK_SECRET_KEY?.trim();
      if (secretKey) {
        const { allowedOrigins, redirectUrls } = storefrontClerkUrls(r.url, brand.domain);
        const configured = await configureClerkInstance(secretKey, { allowedOrigins, redirectUrls });
        if (configured.applied.length) {
          info(pc.dim(`Clerk now allows the deployed origin (${configured.applied.length} settings)`));
        }
        for (const f of configured.failed) info(pc.yellow(`Clerk ${f.step}: ${f.reason}`));
      }
    }

    if (opts.verify !== false && r.url && r.target) {
      await verifyAndRepair(target, brand, r.url, r.target, r.projectName, opts);
    }
  }
}

/**
 * Confirm the deployed storefront serves, and repair it once if it does not.
 *
 * Bounded on purpose: probe, one deterministic repair, re-probe, then stop. A
 * loop here would hammer a host while an operator is not watching, and the
 * second failure of the same repair means the diagnosis was wrong, not that it
 * needs another go.
 */
async function verifyAndRepair(
  storefront: string,
  brand: Brand,
  url: string,
  targetId: TargetId,
  projectName: string,
  opts: { preferred?: TargetId; pushSecrets?: boolean; autofixAgent?: boolean },
): Promise<void> {
  const s = p.spinner();
  s.start("Verifying the deployed storefront");
  let result = await verifyStorefront(url, storefront, process.env);
  s.stop(result.ok ? "Storefront is serving" : `Storefront is not serving — ${result.diagnosis}`);
  info(pc.dim(formatProbes(result.probes)));

  if (result.ok) return;
  info(pc.yellow(result.summary));

  const plan = repairPlanFor(result.diagnosis);
  if (!plan) {
    if (result.diagnosis === "unclassified" && opts.autofixAgent) {
      p.note(
        [
          "No deterministic repair matches this symptom.",
          `Probes: ${formatProbes(result.probes)}`,
          `URL: ${url}`,
          "Re-run with an agent that can read the host logs, or inspect manually.",
        ].join("\n"),
        "Needs investigation",
      );
    }
    process.exitCode = 1;
    return;
  }

  const rs = p.spinner();
  rs.start(`Repairing: ${plan.note}`);
  if (plan.runClerk) await runClerkStep(storefront, brand, url);
  if (plan.rebuild) {
    const rebuilt = await buildSite(storefront, targetId);
    if (!rebuilt) {
      rs.stop("Repair failed at the rebuild step");
      process.exitCode = 1;
      return;
    }
  }
  const redeploy = await deploySite(storefront, brand, {
    preferred: targetId,
    pushSecrets: opts.pushSecrets !== false,
  });
  rs.stop(redeploy.ok ? "Repaired and redeployed" : "Redeploy failed");

  if (!redeploy.ok) {
    info(pc.yellow(redeploy.reason ?? "Redeploy failed."));
    process.exitCode = 1;
    return;
  }

  result = await verifyStorefront(redeploy.url ?? url, storefront, process.env);
  info(pc.dim(formatProbes(result.probes)));
  if (result.ok) {
    info(pc.green("Storefront is serving after repair."));
    return;
  }

  // The repair did not take. Put the previous version back rather than leaving
  // a known-broken site live.
  info(pc.yellow(`Still failing after one repair: ${result.summary}`));
  const rb = await rollback(targetId, storefront, projectName);
  info(rb.ok ? pc.yellow("Rolled back to the previous version.") : pc.yellow(rb.reason ?? "No rollback available."));
  process.exitCode = 1;
}

/**
 * Provision Clerk, write the keys locally, and configure the instance.
 *
 * Called twice in a full deploy, and the split matters. Pre-build, so the
 * publishable key is in `.env.local` before Next inlines it. Post-deploy, so
 * the real `*.workers.dev` origin can be whitelisted — before the deploy that
 * URL does not exist yet, and the earlier single call passed `undefined`, which
 * meant the deployed origin was never allowed.
 */
async function runClerkStep(storefront: string, brand: Brand, deployedUrl?: string): Promise<void> {
  const s = p.spinner();
  s.start("Provisioning Clerk");
  const result = await provisionClerk({
    name: brand.name,
    domain: brand.domain,
    env: process.env,
    storefront,
    cli: clerkCliDeps(),
  });

  if (!result.ok || !result.credentials) {
    s.stop("Clerk not configured");
    if (result.reason) info(pc.yellow(result.reason));
    if (result.guidance) p.note(result.guidance.join("\n"), "Set up Clerk");
    return;
  }

  const { credentials } = result;
  s.stop(
    {
      platform: `Clerk application created (${credentials.applicationId ?? "new"})`,
      "cli-keyless": "Clerk keyless application minted — claim it at dashboard.clerk.com to keep it",
      discovered: "Clerk keys found on this machine",
      manual: "Clerk not configured",
    }[result.tier],
  );

  const written = await mergeEnvLocal(storefront, {
    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: credentials.publishableKey,
    CLERK_SECRET_KEY: credentials.secretKey,
  });
  if (written.length) info(pc.dim(`Wrote to .env.local: ${written.join(", ")}`));

  const { allowedOrigins, redirectUrls } = storefrontClerkUrls(deployedUrl, brand.domain);
  const configured = await configureClerkInstance(credentials.secretKey, {
    allowedOrigins,
    redirectUrls,
  });
  if (configured.applied.length) info(pc.dim(`Clerk configured: ${configured.applied.length} settings`));
  for (const f of configured.failed) info(pc.yellow(`Clerk ${f.step}: ${f.reason}`));
}

async function interactiveSiteCreate(): Promise<void> {
  const brand = await promptBrand(CARRIER_BRAND);
  const dir = (await p.text({
    message: "Output directory",
    placeholder: "./storefront",
    defaultValue: "./storefront",
  })) as string;
  if (p.isCancel(dir)) return;
  const target = resolve(dir);
  if (await exists(target)) {
    const go = await p.confirm({ message: `${target} exists — write into it anyway?`, initialValue: false });
    if (p.isCancel(go) || !go) {
      p.outro("Stopped. Re-run with a different directory.");
      return;
    }
  }
  await doSiteCreate(target, brand);
  p.outro(pc.green(`Scaffolded. cd ${dir} && pnpm install && pnpm dev`));
}

const program = new Command();

program
  .name("carrier")
  .description(
    [
      "Carrier CLI — status, OAuth-ready MCP install, storefront scaffold, and fleet control.",
      "",
      "Domains: " + CLI_DOMAINS.map((d) => d.name).join(", "),
      'Anything they do not cover: carrier ask "…"',
    ].join("\n"),
  )
  .version(VERSION)
  .action(async () => {
    header();
    await runHome({
      onInstall: doPluginInstall,
      onSiteCreate: interactiveSiteCreate,
    });
  });

program
  .command("init")
  .description("Interactive home: status, install plugin+MCP (OAuth on first use), optional storefront.")
  .option("--dir <path>", "Storefront output directory", "./storefront")
  .option("--yes", "Use Carrier defaults, no prompts (plugin + scaffold + build)")
  .option("--full", "After install, continue into storefront scaffold (default interactive path offers both)")
  .action(async (o: { dir: string; yes?: boolean; full?: boolean }) => {
    header();

    if (o.yes) {
      await doPluginInstall();
      const brand = CARRIER_BRAND;
      const target = resolve(o.dir);
      if (await exists(target)) {
        info(pc.yellow(`${target} exists — writing into it (--yes).`));
      }
      await doSiteCreate(target, brand);
      await maybeBuildDeploy(target, brand, { install: true, build: true, deploy: false });
      p.note(oauthFirstUseNote(), "Auth");
      p.note(
        [
          `cd ${o.dir}`,
          `Edit src/brand.config.ts to re-brand anytime`,
          `pnpm dev   # local preview`,
          `MCP endpoint: ${MCP_URL}`,
          'In Claude: "Show my fleet health" (browser OAuth on first use)',
        ].join("\n"),
        "Next",
      );
      p.outro(pc.green("Done. Your connectivity business is wired."));
      return;
    }

    // Default init = first-class TUI home (status + auth helpers + optional scaffold).
    // --full jumps straight into classic plugin+storefront wizard after a status line.
    if (o.full) {
      const st = await gatherStatus();
      printStatus(st);
      await doPluginInstall();

      const brand = await promptBrand(CARRIER_BRAND);
      const target = resolve(o.dir);
      if (await exists(target)) {
        const go = await p.confirm({ message: `${target} exists — write into it anyway?`, initialValue: false });
        if (p.isCancel(go) || !go) {
          p.outro("Stopped. Re-run with --dir <new path>.");
          return;
        }
      }
      await doSiteCreate(target, brand);

      const next = await p.select({
        message: "Roll it out now?",
        options: [
          { value: "build", label: "Install deps + build" },
          { value: "deploy", label: "Install + build + deploy to Cloudflare Workers" },
          { value: "none", label: "Just scaffold — I'll build later" },
        ],
        initialValue: "build",
      });
      if (p.isCancel(next)) process.exit(0);
      await maybeBuildDeploy(target, brand, {
        install: next !== "none",
        build: next !== "none",
        deploy: next === "deploy",
      });

      p.note(oauthFirstUseNote(), "Auth");
      p.note(
        [
          `cd ${o.dir}`,
          `Edit src/brand.config.ts to re-brand anytime`,
          `pnpm dev   # local preview`,
          `MCP endpoint: ${MCP_URL}`,
        ].join("\n"),
        "Next",
      );
      p.outro(pc.green("Done. Your connectivity business is wired."));
      return;
    }

    await runHome({
      onInstall: doPluginInstall,
      onSiteCreate: async () => {
        const brand = await promptBrand(CARRIER_BRAND);
        const target = resolve(o.dir);
        if (await exists(target)) {
          const go = await p.confirm({ message: `${target} exists — write into it anyway?`, initialValue: false });
          if (p.isCancel(go) || !go) {
            p.log.info("Skipped storefront. Pick Install or Exit from the menu, or re-run with --dir.");
            return;
          }
        }
        await doSiteCreate(target, brand);
        const next = await p.select({
          message: "Roll it out now?",
          options: [
            { value: "build", label: "Install deps + build" },
            { value: "deploy", label: "Install + build + deploy to Cloudflare Workers" },
            { value: "none", label: "Just scaffold — I'll build later" },
          ],
          initialValue: "build",
        });
        if (p.isCancel(next)) return;
        await maybeBuildDeploy(target, brand, {
          install: next !== "none",
          build: next !== "none",
          deploy: next === "deploy",
        });
        p.note(
          [
            `cd ${o.dir}`,
            `pnpm dev`,
            `MCP: ${MCP_URL} · OAuth on first use in Claude`,
          ].join("\n"),
          "Next",
        );
      },
    });
  });

const plugin = program.command("plugin").description("Manage the Carrier Claude Code plugin.");
plugin
  .command("install")
  .description("Install/register the Carrier plugin + zero-cred MCP (OAuth on first use).")
  .action(async () => {
    header();
    await doPluginInstall();
    p.outro(pc.green("Plugin ready. Restart Claude Code, then talk to your fleet."));
  });
plugin
  .command("status")
  .description("Show plugin + MCP registration + auth next steps.")
  .action(async () => {
    header();
    const st = await gatherStatus();
    printStatus(st);
    p.note(oauthFirstUseNote(), "Auth");
    p.outro("");
  });

program
  .command("status")
  .description("Show MCP / plugin / auth status and next steps.")
  .action(async () => {
    header();
    const s = p.spinner();
    s.start("Checking status");
    const st = await gatherStatus();
    s.stop("Done");
    p.note(formatStatusBlock(st), "Status");
    p.note(formatNextSteps(st), "Next steps");
    p.note(oauthFirstUseNote(), "Auth");
    p.outro("");
  });

program
  .command("login")
  .description("Sign in from this terminal. Opens a browser once, then refreshes silently.")
  .action(async () => {
    header();
    const s = p.spinner();
    s.start("Waiting for browser sign-in");
    try {
      const r = await oauthLogin({
        openBrowser: (url) => openUrl(url),
        onUrl: (url) => s.message(`Sign in at ${url.slice(0, 60)}…`),
      });
      s.stop(r.ok ? "Signed in" : "Failed");
      if (!r.ok) {
        p.log.error(r.message);
        process.exitCode = 1;
        return;
      }
      p.note(
        [`Scope: ${r.scope ?? "read write"}`, "", "Try it: carrier intelligence fleet-health"].join("\n"),
        "Signed in",
      );
    } catch (e) {
      s.stop("Failed");
      p.log.error(
        withNextStep(e instanceof Error ? e.message : String(e), [
          "Check outbound HTTPS to mcp.carrier.llc",
          "Headless/CI instead: export CARRIER_API_KEY=ak_…",
        ]),
      );
      process.exitCode = 1;
    }
    p.outro("");
  });

/**
 * Clack prompts adapted to `SignupIO`.
 *
 * Clack reports cancellation with a symbol rather than by throwing, so every
 * value is checked with `p.isCancel` and mapped to null. Missing that turns a
 * ctrl-C into the literal string "Symbol(clack:cancel)" submitted as someone's
 * first name.
 */
function clackSignupIO(): SignupIO {
  return {
    async text(message, opts) {
      const value = await p.text({ message, ...(opts?.placeholder ? { placeholder: opts.placeholder } : {}) });
      return p.isCancel(value) ? null : String(value ?? "").trim();
    },
    async password(message) {
      const value = await p.password({ message });
      return p.isCancel(value) ? null : String(value ?? "");
    },
    async confirm(message) {
      const value = await p.confirm({ message });
      return p.isCancel(value) ? null : Boolean(value);
    },
    step(message) {
      // A clack spinner cannot run here: this flow prompts between steps, and a
      // live spinner and a prompt fight for the same lines.
      p.log.step(message);
    },
    note(body, title) {
      p.note(body, title);
    },
  };
}

program
  .command("signup")
  .description("Create a Carrier account from this terminal, then sign in.")
  .action(async () => {
    header();
    const result = await headlessSignup({ io: clackSignupIO() });

    if (!result.ok) {
      if (result.cancelled) {
        p.log.info(result.message);
      } else {
        p.log.error(result.message);
        process.exitCode = 1;
      }
      p.outro("");
      return;
    }

    p.note(
      [
        `Account: ${result.email ?? "created"}`,
        `Organization: ${result.organizationName ?? result.organizationId ?? "created"}`,
      ].join("\n"),
      "Signed up",
    );

    // The account exists but this terminal holds no credential yet. Sign in
    // here rather than printing "now run carrier login": the browser hop is
    // one round trip either way, and stopping short would leave the command
    // half-finished at the exact moment it looks done.
    const s = p.spinner();
    s.start("Signing in");
    try {
      const login = await oauthLogin({
        openBrowser: (url) => openUrl(url),
        onUrl: (url) => s.message(`Finish sign-in at ${url.slice(0, 60)}\u2026`),
      });
      s.stop(login.ok ? "Signed in" : "Sign-in did not finish");
      if (login.ok) {
        p.note("Try it: carrier intelligence fleet-health", "Next");
      } else {
        p.log.warn(withNextStep(login.message, ["Sign in when ready: carrier login"]));
      }
    } catch (e) {
      s.stop("Sign-in did not finish");
      p.log.warn(
        withNextStep(e instanceof Error ? e.message : String(e), [
          "Your account exists — sign in when ready: carrier login",
        ]),
      );
    }
    p.outro("");
  });

program
  .command("logout")
  .description("Forget the stored sign-in on this machine.")
  .action(async () => {
    header();
    await oauthLogout();
    p.log.success("Signed out.");
    p.outro("");
  });

program
  .command("whoami")
  .description("Show the account, org and scopes the current credential resolves to.")
  .option("--json", "Print the raw response on stdout and nothing else")
  .action(async (opts: { json?: boolean }) => {
    if (!opts.json) header();
    const s = opts.json ? null : p.spinner();
    s?.start("Calling environment_info");
    const r = await callMcpTool("environment_info", {}, {
      errorHints: ["Sign in first: carrier login", "Or export CARRIER_API_KEY=ak_…"],
    });
    s?.stop(r.ok ? "Done" : "Failed");
    if (!r.ok) {
      if (opts.json) console.error(r.text);
      else p.log.error(r.text);
      process.exitCode = 1;
      return;
    }
    if (opts.json) {
      process.stdout.write(`${r.text}\n`);
      return;
    }
    p.note(r.text, "environment_info");
    p.outro("");
  });

const connectCmd = program.command("connect").description("Stripe Connect onboarding and status.");

connectCmd
  .command("status")
  .description("Show the authenticated operator's Stripe Connect status.")
  .option("--json", "Print raw JSON")
  .action(async (opts: { json?: boolean }) => {
    if (!opts.json) header();
    const r = await callMcpTool("stripe_connect_status", {}, {
      errorHints: ["Sign in first: carrier login", "Start onboarding: carrier connect begin"],
    });
    if (!r.ok) {
      if (opts.json) console.error(r.text); else p.log.error(r.text);
      process.exitCode = 1;
      return;
    }
    if (opts.json) process.stdout.write(`${r.text}
`);
    else { p.note(r.text, "Stripe Connect"); p.outro(""); }
  });

connectCmd
  .command("begin")
  .description("Start or resume Stripe Connect onboarding and open the secure Stripe flow.")
  .option("--type <type>", "express = new Stripe + KYC; standard = OAuth onto an existing verified Stripe", "standard")
  .option("--no-open", "Print the onboarding URL instead of opening it")
  .action(async (opts: { type: string; open: boolean }) => {
    header();
    if (!['express', 'standard'].includes(opts.type)) {
      p.log.error("--type must be express or standard"); process.exitCode = 2; return;
    }
    const r = await callMcpTool("stripe_connect_begin", { account_type: opts.type }, {
      errorHints: ["Sign in first: carrier login", "Check status: carrier connect status"],
    });
    if (!r.ok) { p.log.error(r.text); process.exitCode = 1; return; }
    let data: { onboarding_url?: string; account_id?: string; account_created?: boolean } = {};
    try { data = JSON.parse(r.text) as typeof data; } catch { /* show raw below */ }
    if (!data.onboarding_url) { p.log.error(r.text); process.exitCode = 1; return; }
    if (opts.open) {
      const opened = await openUrl(data.onboarding_url);
      if (opened.ok) ok(opened.hint); else p.note(data.onboarding_url, "Open this secure Stripe URL");
    } else {
      process.stdout.write(`${data.onboarding_url}\n`);
    }
    p.note([
      `Account: ${data.account_id ?? "created by Stripe"}`,
      data.account_created ? "A new connected account was created." : "Resuming the existing account.",
      "After Stripe: carrier connect status",
    ].join("\n"), "Onboarding");
    p.outro("");
  });

const openCmd = program.command("open").description("Open Carrier account / product URLs in your browser.");
for (const [name, url, desc] of [
  ["signup", SIGN_UP_URL, "Create a Carrier account in a browser (headless: carrier signup)"],
  ["signin", SIGN_IN_URL, "Sign in to Carrier"],
  ["console", CONSOLE_URL, "Open the Carrier console"],
  ["onboarding", ONBOARDING_URL, "Open console onboarding (link OCS / managed)"],
  ["mcp", MCP_HOME, "Open mcp.carrier.llc product page"],
] as const) {
  openCmd
    .command(name)
    .description(desc)
    .action(async () => {
      header();
      const r = await openUrl(url);
      if (r.ok) ok(r.hint);
      else {
        p.log.warn(r.hint);
        p.note(url, "Open this URL");
      }
      p.note(accountLinksNote(), "Account links");
      p.outro("");
    });
}

program
  .command("examples")
  .description("Print natural-language fleet prompts for Claude / MCP.")
  .action(async () => {
    header();
    p.note(formatNlExamples(), "Talk to fleet — paste into Claude");
    p.note(
      [
        "After `carrier plugin install` (or this menu → Install):",
        "  1. Open Claude Code",
        '  2. Say: "Show my fleet health"',
        "  3. Browser opens for sign-in/sign-up on first use",
        "",
        "Slash commands: /carrier:fleet  /carrier:status  /carrier:wallet  /carrier:onboard",
      ].join("\n"),
      "How",
    );
    p.note(
      [
        "With CARRIER_API_KEY set, every capability also has its own command:",
        ...CLI_DOMAINS.map((d) => `  carrier ${d.name} --help`.padEnd(34) + pc.dim(d.summary)),
        "",
        "  --json   raw response on stdout, ready for jq",
        "  --commit apply a change instead of previewing it",
      ].join("\n"),
      "Domain commands",
    );
    p.outro("");
  });

program
  .command("ask")
  .description(
    'Natural-language fallback for whatever the domain commands miss: carrier ask "show fleet health" (needs CARRIER_API_KEY or OCS token).',
  )
  .argument("<intent...>", "Natural-language intent")
  .action(async (parts: string[]) => {
    header();
    const intent = parts.join(" ").trim();
    if (!intent) {
      fail("Missing intent.", [
        'carrier ask "show fleet health"',
        "Or use Claude interactively (no token): carrier plugin install",
      ]);
      process.exitCode = 1;
      return;
    }
    const s = p.spinner();
    s.start("Asking Carrier MCP…");
    const result = await carrierAsk(intent);
    s.stop(result.ok ? "Done" : "Failed");
    if (result.ok) {
      p.note(result.text.slice(0, 6000), "Answer");
      p.outro("");
    } else {
      p.log.error(result.text);
      p.outro(pc.yellow("See next steps above."));
      process.exitCode = 1;
    }
  });

// Discrete commands per capability, grouped by the console's domains.
// Each one calls a single MCP tool through the same session handshake as `ask`.
// A space in a command name nests one level: "sim-status set" registers as
// `carrier subscribers sim-status set`, sharing the "sim-status" group with
// its sibling "sim-status show".
for (const domain of CLI_DOMAINS) {
  const group = program.command(domain.name).description(domain.summary);
  const subgroups = new Map<string, Command>();
  for (const cmd of domain.commands) {
    const parts = cmd.name.split(" ");
    let parent = group;
    let prefix = "";
    for (const part of parts.slice(0, -1)) {
      prefix = prefix ? `${prefix} ${part}` : part;
      let nested = subgroups.get(prefix);
      if (!nested) {
        nested = parent.command(part).description(`${part} subcommands`);
        subgroups.set(prefix, nested);
      }
      parent = nested;
    }
    const sub = parent
      .command(parts[parts.length - 1]!)
      .description(
        cmd.write
          ? `${cmd.summary} Dry run unless --commit.`
          : cmd.requireConfirm
            ? `${cmd.summary} Refuses without --confirm.`
            : cmd.summary,
      );
    for (const opt of cmd.options) sub.option(opt.flags, opt.description);
    sub.option("--json", "Print the raw tool response on stdout and nothing else");
    if (cmd.write) sub.option("--commit", "Apply the change instead of previewing it");
    // REST endpoints answer 202 with a single-use token the caller re-sends,
    // which is how apps/api gates its destructive routes.
    if (cmd.transport === "rest") {
      sub.option("--confirm-token <token>", "Token from a prior 202, to confirm this call");
    }
    // No dry_run on the tool, so there is nothing to preview: the flag is the
    // only thing standing between a typo and a real change.
    if (cmd.requireConfirm) sub.option("--confirm", "Required. This call takes effect immediately.");
    sub.action(async (opts: Record<string, unknown> & CapabilityFlags) => {
      if (opts.json !== true) header();
      await runCapability(domain, cmd, opts);
      if (opts.json !== true) p.outro("");
    });
  }
}

// Escape hatch. The generated commands cover every tool the server registered
// when they were last generated; this covers the remainder — a tool added
// since, or one whose schema the generator flattened badly.
program
  .command("tool <name>")
  .description("Call one MCP tool directly, by name, with raw arguments")
  .option(
    "--arg <key=value>",
    "One tool argument. Repeat for more. Dots nest: context.iccid=8931…",
    (value: string, prev: string[]) => [...prev, value],
    [] as string[],
  )
  .option("--json", "Print the raw tool response on stdout and nothing else")
  .action(async (name: string, o: { arg: string[] } & CapabilityFlags) => {
    if (o.json !== true) header();
    await runRawTool(name, o.arg, o);
    if (o.json !== true) p.outro("");
  });

program
  .command("dash [domain]")
  .description(`Render a status screen in the terminal: ${DASH_DOMAINS.join(" | ")}`)
  .option("--json", "Emit the screen model as JSON instead of drawing it")
  .option("--no-color", "Plain text, safe to pipe")
  .option("--width <n>", "Terminal width override")
  .action(async (domain: string | undefined, o: { json?: boolean; color?: boolean; width?: string }) => {
    const target = domain ?? "fleet";
    if (!isDashDomain(target)) {
      fail(`Unknown dashboard "${target}".`, [`Available: ${DASH_DOMAINS.join(", ")}`]);
      process.exitCode = 1;
      return;
    }
    const tool = DASH_TOOL[target];
    if (!tool) {
      fail(`\`carrier dash ${target}\` reads local state, not a tool.`, [
        "Run it from a storefront directory: carrier site deploy --no-secrets",
      ]);
      process.exitCode = 1;
      return;
    }
    if (!o.json) header();
    const result = await callMcpTool(tool, {}, {
      errorHints: [`Check auth: carrier status`, `Or ask in words: carrier ask "…"`],
    });
    if (!result.ok) {
      fail(result.text, ["carrier status"]);
      process.exitCode = 1;
      return;
    }
    // Prefer a screen the server already built — that is the whole point of the
    // shared model. Fall back to deriving one from a JSON payload; prose cannot
    // drive a screen and is reported honestly as no data.
    let payload: unknown;
    try {
      payload = JSON.parse(result.text);
    } catch {
      payload = undefined;
    }
    const screen = screenFromStructured(result.structured) ?? buildDashScreen(target, payload);
    if (o.json) {
      process.stdout.write(`${JSON.stringify(screen, null, 2)}\n`);
      return;
    }
    process.stdout.write(
      `${renderTui(screen, {
        color: o.color !== false,
        width: o.width ? Number(o.width) : process.stdout.columns,
      })}\n`,
    );
  });

const site = program.command("site").description("Scaffold and deploy a white-labeled storefront.");
site
  .command("create [dir]")
  .description("Scaffold a white-labeled storefront from the bundled template.")
  .option("--name <name>", "Brand name")
  .option("--domain <domain>", "Domain")
  .option("--accent <hex>", "Accent color")
  .option("--yes", "Carrier defaults, no prompts")
  .action(async (dir: string | undefined, o: { name?: string; domain?: string; accent?: string; yes?: boolean }) => {
    header();
    let brand: Brand = { ...CARRIER_BRAND };
    if (o.name || o.domain || o.accent) {
      const name = o.name ?? brand.name;
      const domain = o.domain ?? (o.name ? `${slug(o.name)}.com` : brand.domain);
      const isCarrier = name === CARRIER_BRAND.name && domain === CARRIER_BRAND.domain;
      brand = {
        ...brand,
        name,
        domain,
        legalName: o.name ?? brand.legalName,
        tagline: o.name ? `Mobile connectivity by ${o.name}.` : brand.tagline,
        description: o.name
          ? `Mobile data from ${o.name}. Instant QR activation, no contract.`
          : brand.description,
        supportEmail: `support@${domain}`,
        supportUrl: `https://${domain}/help`,
        supportWhatsapp: isCarrier ? brand.supportWhatsapp : "",
        emailFromName: name,
        appUrl: `https://${domain}`,
        social: isCarrier ? brand.social : {},
        colors: {
          ...brand.colors,
          accent: o.accent ?? brand.colors.accent,
          accentDark: o.accent ? deriveAccentDark(o.accent) : brand.colors.accentDark,
        },
      };
    } else if (!o.yes) {
      brand = await promptBrand(CARRIER_BRAND);
    }
    const target = resolve(dir ?? "./storefront");
    if (await exists(target)) {
      const go = await p.confirm({ message: `${target} exists — write into it anyway?`, initialValue: false });
      if (p.isCancel(go) || !go) {
        p.outro("Stopped. Re-run with a different directory.");
        return;
      }
    }
    try {
      await doSiteCreate(target, brand);
    } catch {
      process.exitCode = 1;
      return;
    }
    p.outro(pc.green(`Scaffolded. cd ${dir ?? "storefront"} && pnpm install && pnpm dev`));
  });
site
  .command("deploy [dir]")
  .description("Build + deploy a storefront to whichever host is available on this box.")
  .option("--name <name>", "Project/worker name (defaults from brand)")
  .option(`--target <target>`, `Force a host: ${TARGET_IDS.join(" | ")}`)
  .option("--no-secrets", "Skip staging runtime secrets on the host")
  .option("--clerk", "Provision and configure Clerk before building")
  .option("--no-verify", "Skip the post-deploy check that the site actually serves")
  .option("--autofix-agent", "On an unrecognised failure, print an agent-ready diagnosis bundle")
  .option("--domain", "Route the brand domain to the deployment (Cloudflare provisions DNS + cert)")
  .action(
    async (
      dir: string | undefined,
      o: {
        name?: string;
        target?: string;
        secrets?: boolean;
        clerk?: boolean;
        verify?: boolean;
        autofixAgent?: boolean;
        domain?: boolean;
      },
    ) => {
      header();
      const target = resolve(dir ?? "./storefront");
      if (o.target && !isTargetId(o.target)) {
        fail(`Unknown target "${o.target}".`, [`Supported: ${TARGET_IDS.join(", ")}`]);
        process.exitCode = 1;
        return;
      }
      try {
        const brand = await loadStorefrontBrand(target, o.name ? { name: o.name } : undefined);
        await maybeBuildDeploy(target, brand, {
          install: true,
          build: true,
          deploy: true,
          preferred: o.target as TargetId | undefined,
          pushSecrets: o.secrets !== false,
          clerk: o.clerk === true,
          verify: o.verify !== false,
          autofixAgent: o.autofixAgent === true,
          customDomain: o.domain === true,
        });
      } catch (e: unknown) {
        fail(String(e instanceof Error ? e.message : e), [
          "Scaffold first: carrier site create",
          "Check host logins: carrier site targets",
        ]);
        process.exitCode = 1;
        return;
      }
      p.outro("");
    },
  );
site
  .command("targets [dir]")
  .description("Show which deploy hosts are installed, logged in, and ready.")
  .action(async (dir: string | undefined) => {
    header();
    const storefront = resolve(dir ?? "./storefront");
    const statuses = await probeAll(storefront);
    const ranked = rankTargets(statuses);
    const rows = statuses.map((s) => {
      const mark = s.ready ? pc.green("ready") : pc.dim("not ready");
      const detail = s.ready
        ? [s.account, s.configured ? "configured" : undefined].filter(Boolean).join(", ")
        : (s.reason ?? "");
      return `${s.label.padEnd(20)} ${mark}  ${pc.dim(detail)}`;
    });
    p.note(rows.join("\n"), "Deploy hosts");
    info(
      ranked.length
        ? pc.green(`Default on this box: ${ranked[0].label}`)
        : pc.yellow("No host is ready — log in to one, then re-run."),
    );
    p.outro("");
  });
site
  .command("clerk [dir]")
  .description("Create or discover a Clerk instance and configure it for the storefront.")
  .option("--name <name>", "Application name (defaults from brand)")
  .option("--production", "Also create a production instance (Platform API only)")
  .option("--no-create", "Never create a new application; only reuse existing keys")
  .option("--url <url>", "Deployed URL to whitelist as an origin/redirect")
  .action(
    async (
      dir: string | undefined,
      o: { name?: string; production?: boolean; create?: boolean; url?: string },
    ) => {
      header();
      const storefront = resolve(dir ?? "./storefront");
      try {
        const brand = await loadStorefrontBrand(storefront, o.name ? { name: o.name } : undefined);
        const result = await provisionClerk({
          name: o.name ?? brand.name,
          domain: brand.domain,
          production: o.production === true,
          noCreate: o.create === false,
          env: process.env,
          storefront,
          cli: clerkCliDeps(),
        });
        if (!result.ok || !result.credentials) {
          if (result.reason) info(pc.yellow(result.reason));
          p.note((result.guidance ?? []).join("\n"), "Set up Clerk");
          process.exitCode = 1;
          return;
        }
        const written = await mergeEnvLocal(storefront, {
          NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: result.credentials.publishableKey,
          CLERK_SECRET_KEY: result.credentials.secretKey,
        });
        info(
          result.tier === "platform"
            ? pc.green(`Created Clerk application ${result.credentials.applicationId ?? ""}`.trim())
            : pc.green("Reused Clerk keys already on this machine"),
        );
        if (written.length) info(pc.dim(`Wrote to .env.local: ${written.join(", ")}`));

        const { allowedOrigins, redirectUrls } = storefrontClerkUrls(o.url, brand.domain);
        const configured = await configureClerkInstance(result.credentials.secretKey, {
          allowedOrigins,
          redirectUrls,
        });
        if (configured.applied.length) {
          p.note(configured.applied.join("\n"), "Applied to the Clerk instance");
        }
        for (const f of configured.failed) info(pc.yellow(`${f.step}: ${f.reason}`));
      } catch (e: unknown) {
        fail(String(e instanceof Error ? e.message : e), ["Scaffold first: carrier site create"]);
        process.exitCode = 1;
        return;
      }
      p.outro("");
    },
  );


site
  .command("email [dir]")
  .description("Connect Resend in the browser so the storefront can email QR codes.")
  .option("--no-browser", "Never open a browser; print the manual steps instead")
  .option("--yes", "Non-interactive: skip rather than wait for a human")
  .option("--timeout <seconds>", "How long to wait for the browser approval", "180")
  .action(
    async (dir: string | undefined, o: { browser?: boolean; yes?: boolean; timeout?: string }) => {
      header();
      const storefront = resolve(dir ?? "./storefront");
      // A CI scaffold has no human and no browser. It must say so and move on,
      // never sit on a loopback listener until the job times out.
      const interactive = process.stdin.isTTY === true && o.yes !== true;

      try {
        const brand = await loadStorefrontBrand(storefront);
        const result = await provisionResend({
          name: brand.name,
          domain: brand.domain,
          env: process.env,
          noBrowser: o.browser === false || !interactive,
          timeoutMs: Math.max(10, Number(o.timeout) || 180) * 1000,
          deps: {
            fetch,
            openBrowser: (url) => openUrl(url),
            startLoopback: () => startLoopbackServer(),
            notify: (message) => info(pc.dim(message)),
            promptForKey: interactive
              ? async (url) => {
                  const value = await p.text({
                    message: `Paste the Resend API key from ${url}`,
                    placeholder: "re_…",
                  });
                  return p.isCancel(value) ? null : (value as string);
                }
              : undefined,
          },
        });

        if (!result.ok || !result.credentials) {
          if (result.reason) info(pc.yellow(result.reason));
          p.note((result.guidance ?? []).join("\n"), "Set up email delivery");
          // Non-interactive is a skip, not a failure: the storefront works
          // without email, and failing here would break an unattended scaffold.
          if (interactive) process.exitCode = 1;
          p.outro("");
          return;
        }

        // The key itself is never printed, here or anywhere else.
        const written = await mergeEnvLocal(storefront, {
          RESEND_API_KEY: result.credentials.apiKey,
        });
        info(
          result.tier === "oauth"
            ? pc.green(
                `Created a send-only Resend key on your account${
                  result.credentials.domain ? `, restricted to ${result.credentials.domain}` : ""
                }`,
              )
            : result.tier === "discovered"
              ? pc.green("Reused the Resend key already on this machine")
              : pc.green("Saved the Resend key you pasted"),
        );
        if (written.length) info(pc.dim("Wrote RESEND_API_KEY to .env.local"));
        else info(pc.yellow("RESEND_API_KEY already has a value in .env.local — left it alone."));

        if (!result.credentials.domain) {
          info(
            pc.dim(
              `Mail will be sent from ${brand.emailFromMailbox}@${brand.domain}. Verify that domain at ${RESEND_DOMAINS_URL} or it will not be delivered.`,
            ),
          );
        }
      } catch (e: unknown) {
        fail(String(e instanceof Error ? e.message : e), ["Scaffold first: carrier site create"]);
        process.exitCode = 1;
        return;
      }
      p.outro("");
    },
  );

site
  .command("logo [dir]")
  .description("Generate a storefront logo (SVG always; PNG if an image key is on this box or Carrier).")
  .action(async (dir: string | undefined) => {
    header();
    const target = resolve(dir ?? "./storefront");
    try {
      const brand = await loadStorefrontBrand(target);
      const logo = await writeStorefrontLogo(target, brand);
      ok(`Logo (${logo.source}): ${logo.path}`);
    } catch (e: unknown) {
      fail(String(e instanceof Error ? e.message : e), [
        "Scaffold first: carrier site create",
      ]);
      process.exitCode = 1;
      return;
    }
    p.outro("");
  });

program.parseAsync(process.argv).catch((e: unknown) => {
  console.error(pc.red(String(e instanceof Error ? e.message : e)));
  console.error(
    pc.dim(
      [
        "",
        "What to do next:",
        "  → carrier status",
        "  → carrier open signup",
        "  → carrier plugin install",
        "  → carrier --help",
      ].join("\n"),
    ),
  );
  process.exit(1);
});
